import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { INDUSTRIES } from "../data/site";

export const Industries = ({ industries: industriesProp }) => {
  const list = industriesProp || INDUSTRIES;
  const [active, setActive] = useState(0);
  const item = list[active];

  return (
    <section data-testid="industries-section" className=" scroll-mt-28 relative py-24 md:py-32 bg-navy-900 text-white overflow-hidden ">
      <div className="absolute inset-0 dot-pattern-dark opacity-50 pointer-events-none" />
      <div className="absolute -top-40 -right-20 w-[500px] h-[500px] bg-amber-500/15 rounded-full blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-500 font-bold flex items-center gap-2">
            <span className="w-8 h-px bg-amber-500" />
            Industries
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1]">
            Trusted across
            <br />
              <span className="text-amber-500">critical sectors.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 flex flex-col divide-y divide-white/10 border-y border-white/10">
            {list.map((ind, i) => (
              <button
                key={ind.slug}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                data-testid={`industry-tab-${ind.slug}`}
                className={`group text-left py-6 transition-all ${active === i ? "" : "opacity-60 hover:opacity-100"}`}
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <span className={`text-xs font-mono tracking-tight ${active === i ? "text-amber-500" : "text-slate-500"}`}>
                      0{i + 1}
                    </span>
                    <h3 className={`font-display text-2xl md:text-3xl font-bold tracking-tight transition-colors ${active === i ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
                      {ind.title}
                    </h3>
                  </div>
                  <ArrowUpRight className={`w-5 h-5 transition-all ${active === i ? "text-amber-500" : "text-slate-600 -rotate-45"}`} />
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7 relative lg:sticky lg:top-28 h-fit">
            <div className="relative aspect-[5/4] md:aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 bg-navy-800">
              <AnimatePresence mode="wait">
                <motion.div
                  key={item.slug}
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0"
                >
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`text-${item.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="absolute bottom-0 inset-x-0 p-8 md:p-10"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 mb-5">
                    <item.icon className="w-3.5 h-3.5 text-navy-900" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-navy-900 font-bold">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-white text-lg md:text-xl leading-snug max-w-xl font-display font-semibold tracking-tight">
                    {item.description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;
